import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AgreementAttachmentForm,
    AgreementNoteRemindersForm,
    AgreementPeriodForm,
    AgreementReminderForm,
    AgreementSharedInformationForm,
    FileUploadGroupForm,
    AgreementForm,
    FileUpload,
    AgreementInventoryForm,
    AgreementPaymentForm,
    AgreementRentalForm,
    RealEstateAgreementForm
} from '../../feature/shared-agreement.model';
import { APP_ROUTES } from 'app/constants';
import { CreateAgreementRequest } from '@services/api-client/requests/create-agreement-request.type';
import { switchMap, of, merge } from 'rxjs';
import { CanComponentDeactivate } from '@guards/can-deactivate.guard';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { AgreementNoteReminder } from 'app/types';
import { AgreementBaseComponent } from './agreement-base.component';
import { TemplateResponse } from '@services/api-client/responses';
import { CalendarModalComponent } from '../../feature/components/calendar-modal/calendar-modal.component';
import { AgreementFormHelper } from '../../feature/shared-agreement.helper';

enum TabIndex {
    SelectType = 0,
    GeneralInfo = 1,
    PeriodsAndRenewals = 2,
    NotesAndActivities = 3,
    Attachments = 4
}

@UntilDestroy()
@Component({
    selector: 'app-create-new-agreement',
    templateUrl: './create-new-agreement.component.html',
    styleUrl: './create-new-agreement.component.scss'
})
export class CreateNewAgreementComponent extends AgreementBaseComponent implements OnInit, CanComponentDeactivate {
    public showPotentialValidationMessages = [false, false, false, false];

    protected override inboxId: string;
    protected replacedAgreementId: string;
    private inboxFileProcessed = false;

    public ngOnInit() {
        this.metadataService.clearHighlights();
        this.initializeAgreementForm();
    }

    private initializeAgreementForm() {
        const user = this.authenticationService.getUser();
        this.agreementForm = new FormGroup<AgreementForm>({
            fileUpload: new FormGroup<FileUploadGroupForm>({
                file: new FormControl<FileUpload>(null)
            }),
            details: new FormGroup<AgreementSharedInformationForm>({
                agreementTypeId: new FormControl(1, Validators.required),
                customerId: new FormControl(user.customerId, Validators.required),
                responsibleId: new FormControl(user.userId, Validators.required),
                counterpartId: new FormControl('', Validators.required),
                name: new FormControl(''),
                agreementNo: new FormControl(''),
                category: new FormControl('', Validators.required),
                area: new FormControl('', Validators.required),
                description: new FormControl(''),
                signer: new FormControl(''),
                readers: new FormControl<string[]>([]),
                allReadersAccess: new FormControl(false),
                status: new FormControl(1),
                riskAnalysis: new FormControl('')
            }),
            period: new FormGroup<AgreementPeriodForm>({
                commitmentPeriodTypeId: new FormControl(0, Validators.required),
                validFrom: new FormControl<NgbDate>(null, Validators.required),
                validTo: new FormControl<NgbDate>(null),
                noticePeriod: new FormControl(0, Validators.required),
                noticePeriodTypeId: new FormControl(1, Validators.required),
                noticePeriodAfterExtension: new FormControl(0),
                noticePeriodTypeIdAfterExtension: new FormControl(1),
                periodAfterExtension: new FormControl(0),
                periodTypeIdAfterExtension: new FormControl(1),
                managedDate: new FormControl<NgbDate>(null),
                managedNote: new FormControl(''),
                terminationDate: new FormControl<NgbDate>(null),
                expiryDateAfterTermination: new FormControl<NgbDate>(null),
                replacedByAgreementId: new FormControl<string | null>(null),
                replacedByAgreementName: new FormControl<string | null>(null),
                replacedAgreementId: new FormControl<string | null>(null),
                replacedAgreementName: new FormControl<string | null>(null)
            }),
            reminder: new FormGroup<AgreementReminderForm>({
                recipients: new FormControl<string[]>([]),
                reminderPeriod: new FormControl(1),
                numOfReminders: new FormControl(1),
                escalatedRecipients: new FormControl<string[]>([]),
                addToCalendar: new FormControl<boolean>(false)
            }),
            notesReminders: new FormGroup<AgreementNoteRemindersForm>({
                notes: new FormControl<AgreementNoteReminder[]>([])
            }),
            attachments: new FormGroup<AgreementAttachmentForm>({
                attachments: new FormControl<FileUpload[]>([])
            })
        });

        this.checkAgreementFileChanged();
        this.checkQueryParams();
        this.agreementForm.controls.details.controls.agreementTypeId.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.checkAgreementTypeTemplate();
            });
        this.checkAgreementTypeTemplate();
    }

    private checkAgreementTypeTemplate() {
        const typeId = this.agreementForm.controls.details.controls.agreementTypeId.value;
        this.apiClientService.getAgreementTemplateByType$(typeId).subscribe({
            next: (response: TemplateResponse) => {
                // Remove form groups if they exist
                if (this.f.payment) {
                    this.agreementForm.removeControl('payment');
                }
                if (this.f.realEstate) {
                    this.agreementForm.removeControl('realEstate');
                }
                if (this.f.rental) {
                    this.agreementForm.removeControl('rental');
                }
                if (this.f.inventory) {
                    this.agreementForm.removeControl('inventory');
                }

                // Add form groups based on template response
                if (response?.payment) {
                    this.agreementForm.addControl(
                        'payment',
                        new FormGroup<AgreementPaymentForm>({
                            paymentTerms: new FormControl(30),
                            customPaymentTerms: new FormControl(0),
                            value: new FormControl(0),
                            currency: new FormControl('SEK'),
                            invoiceIntervalId: new FormControl(0)
                        })
                    );
                }
                if (response?.realEstate) {
                    this.agreementForm.addControl(
                        'realEstate',
                        new FormGroup<RealEstateAgreementForm>({
                            propertyNo: new FormControl(''),
                            apartmentNo: new FormControl(''),
                            size: new FormControl(0),
                            typeOfPremises: new FormControl('')
                        })
                    );
                }
                if (response?.rental) {
                    this.agreementForm.addControl(
                        'rental',
                        new FormGroup<AgreementRentalForm>({
                            employeeNo: new FormControl(''),
                            employeeName: new FormControl(''),
                            grossSalaryDeduction: new FormControl(0),
                            benefitValue: new FormControl(0),
                            additionalInsurance: new FormControl(false),
                            registrationNo: new FormControl(''),
                            carBrand: new FormControl(''),
                            carModel: new FormControl(''),
                            modelYear: new FormControl(new Date().getFullYear()),
                            mileageAnnual: new FormControl(1500)
                        })
                    );
                }
                if (response?.inventory) {
                    this.agreementForm.addControl(
                        'inventory',
                        new FormGroup<AgreementInventoryForm>({
                            inventoryNumber: new FormControl(''),
                            typeOfInventory: new FormControl(''),
                            condition: new FormControl(false),
                            location: new FormControl(''),
                            benefitValue: new FormControl(0),
                            warranty: new FormControl(false),
                            serialNumber: new FormControl(''),
                            manufacturer: new FormControl(''),
                            model: new FormControl(''),
                            modelYear: new FormControl(new Date().getFullYear()),
                            user: new FormControl(''),
                            purchaseCost: new FormControl(0)
                        })
                    );
                    // Set default Indefinitely
                    this.periodForm.controls.commitmentPeriodTypeId.setValue(-1);
                }
            }
        });
    }

    private checkQueryParams() {
        this.route.queryParams.subscribe((params) => {
            const url = params['fileurl'] as string;
            const filename = params['filename'] as string;
            this.inboxId = params['id'] as string;
            this.replacedAgreementId = params['replaceagreementid'] as string;
            const agreementTypeId =
                params['agreementtypeid'] !== undefined ? Number(params['agreementtypeid']) : undefined;
            if (agreementTypeId) {
                this.f.details.controls.agreementTypeId.setValue(agreementTypeId);
                setTimeout(() => {
                    this.next();
                    this.checkAgreementTypeTemplate();
                }, 0);
            }
            if (filename) {
                this.f.fileUpload.controls.file.setValue({
                    url: url,
                    fileName: filename,
                    fileExtension: 'pdf'
                });
            }
        });
    }

    public previous() {
        this.tabIndex--;
        this.showPotentialValidationMessages[this.tabIndex] = false;
        const scrollableElement = document.querySelector('.main-content-area');
        if (scrollableElement) {
            scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    public next() {
        this.showPotentialValidationMessages[this.tabIndex] = true;
        if (this.isCurrentFormValid()) {
            this.tabIndex++;
            // Show metadata popup after user progresses past step 1 (if file was from inbox)
            if (this.inboxId && !this.inboxFileProcessed) {
                this.showMetadataPopupIfEligible();
                // Mark inbox file as processed so subsequent manual uploads work normally
                this.inboxFileProcessed = true;
            }
            const scrollableElement = document.querySelector('.main-content-area');
            if (scrollableElement) {
                scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }

    public showContent(tab: TabIndex) {
        if (this.tabIndex == (tab as number)) {
            return true;
        }
        return false;
    }

    public isCurrentFormValid() {
        return {
            [this.tabs.SelectType]: this.agreementForm.controls.details.controls.agreementTypeId.valid,
            [this.tabs.GeneralInfo]: this.detailsForm.valid,
            [this.tabs.PeriodsAndRenewals]: this.agreementForm.controls.period.valid,
            [this.tabs.NotesAndActivities]: this.agreementForm.controls.notesReminders.valid,
            [this.tabs.Attachments]: this.agreementForm.controls.attachments.valid
        }[this.tabIndex];
    }

    public cancel() {
        this.navigateToAgreementList();
    }

    private navigateToAgreementList() {
        if (this.f.details.controls.agreementTypeId.value === 13) {
            void this.router.navigate([`/${APP_ROUTES.InventoryList}`]);
            return;
        }
        void this.router.navigate([`/${APP_ROUTES.AgreementList}`]);
    }

    public save() {
        if (this.agreementForm.invalid) return;

        const request: CreateAgreementRequest = {
            agreementTypeId: this.agreementForm.controls.details.value.agreementTypeId,
            fileName: this.getFileName(this.f.fileUpload.value.file),
            tempPath: this.f.fileUpload?.value?.file?.url,
            agreementNo: this.f.details.value.agreementNo,
            name: this.f.details.value.name,
            description: this.f.details.value.description,
            customerId: this.f.details.value.customerId,
            counterpartId: this.f.details.value.counterpartId,
            responsibleId: this.f.details.value.responsibleId,
            area: this.f.details.value.area,
            category: this.f.details.value.category,
            signer: this.f.details.value.signer,
            readers: this.f.details.value.readers,
            allReadersAccess: this.f.details.value.allReadersAccess,
            riskAnalysis: this.f.details.value.riskAnalysis,
            replacedAgreementId: this.replacedAgreementId
        };

        let createdAgreementId: string;

        this.apiClientService
            .createAgreement$(request)
            .pipe(
                untilDestroyed(this),
                switchMap((res) => {
                    createdAgreementId = res.id;
                    const id = res.id;
                    return of(id).pipe(
                        this.storeAttachments(id),
                        this.storeAgreementPeriod(id),
                        this.storeReminderDetails(id),
                        this.storePaymentDetails(id),
                        this.storeRealEstateDetails(id),
                        this.storeRentalDetails(id),
                        this.storeInventoryDetails(id),
                        this.storeNoteReminders(id),
                        switchMap(() => {
                            if (this.inboxId) {
                                return this.apiClientService.deleteInboxItem$(this.inboxId);
                            }
                            return of(null);
                        })
                    );
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.AGREEMENTS.TOAST_CREATE_SUCCESS.MESSAGE'
                    ) as string;
                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.agreementForm.markAsPristine();

                    const hasReminders = (this.reminderForm.controls.recipients.value?.length ?? 0) > 0;
                    if (hasReminders && this.reminderForm.controls.addToCalendar?.value) {
                        this.openPostSaveCalendarModal(createdAgreementId);
                    } else {
                        this.navigateToAgreementList();
                    }
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.AGREEMENTS.TOAST_CREATE_ERROR.MESSAGE'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    private openPostSaveCalendarModal(agreementId: string): void {
        const noticeDate = AgreementFormHelper.getNoticeDate(this.periodForm.controls);
        const dates = AgreementFormHelper.computeReminderDates(
            noticeDate,
            this.reminderForm.controls.reminderPeriod.value ?? 0,
            this.reminderForm.controls.numOfReminders.value ?? 1
        );
        const agreementUrl =
            window.location.origin +
            this.router.serializeUrl(this.router.createUrlTree(['/agreement/details', agreementId]));

        const ref = this.modalService.open(CalendarModalComponent, { size: 'lg', backdrop: 'static' });
        const instance = ref.componentInstance as CalendarModalComponent;
        instance.dates = dates;
        instance.agreementName = this.f.details.value.name ?? '';
        instance.agreementUrl = agreementUrl;

        merge(ref.closed, ref.dismissed)
            .pipe(untilDestroyed(this))
            .subscribe(() => this.navigateToAgreementList());
    }

    private getFileName(file: FileUpload) {
        return file?.fileName ? file.fileName : '';
    }

    public override handleFailedCounterparts(failedCounterpartNames: string[]): void {
        // Switch to GeneralInfo tab where AgreementPartsComponent is located
        this.tabIndex = this.tabs.GeneralInfo;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        // The base class will set the pending counterpart in the metadata service
        super.handleFailedCounterparts(failedCounterpartNames);
    }
}
